/* eslint-disable  @typescript-eslint/no-explicit-any */
import React, { useState, useMemo, JSX } from "react";
// Ensure React is imported to access JSX.Element type
import { Typography } from "@mui/material";
import { Formik, Form } from "formik";
import {
  TextInputComponent,
  AlertComponent,
  CountryCodeWithPhoneNumber,
} from "../../../../common/components";
import {
  getInitialValues,
  validationSchema,
} from "./user-requests-add-user.utils";
import { useSetLoading } from "../../../../common/hooks/appearance";
import "./user-requests-add-user.scss";
import { AlertMessage } from "../../../tenant/tenant-details/tenant-details-types";
import { useLogin } from "../../../../common/hooks/useUser";
import { updateTenantUsers } from "../../../../api/settings";
import RoleSelect from "../components/role-select/role-select";
import closeIcon from "../../../../assets/icons/closeIcon.svg";

interface UserRequestsListAddProps {
  roles: { value: number; label: string }[];
  onSuccess?: () => void;
  setIsAddOpen: () => void;
  rowData?: any;
}

const UserRequestsAddUser: React.FC<UserRequestsListAddProps> = ({
  roles,
  onSuccess,
  setIsAddOpen,
  rowData,
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = React.useState<AlertMessage>({
    open: false,
    message: "",
    type: undefined,
  });
  const [status] = useState("ACTIVE");

  const handleCancel = async () => {
    setIsAddOpen();
  };

  const tenantDetails = useLogin();

  const currentTenantId = tenantDetails?.tenantDetails?.id;

  const updateTenantUserDetails = async (data: any, resetForm: any) => {
    const tenantId = currentTenantId;
    try {
      setLoading(true);
      const payload = {
        users: [
          {
            id: data?.id ?? null,
            firstName: data?.firstName,
            lastName: data?.lastName,
            email: data?.email,
            phoneNumber:
              data?.phoneNumber?.trim() === "" ? null : data?.phoneNumber,
            status: status,
            roleId: data?.role,
            isPrimaryUser: data?.role?.role === "Super Admin" ? true : false,
            requestedAccess: true,
          },
        ],
      };

      const resp = await updateTenantUsers(
        parseInt(tenantId),
        payload,
        "User Requests"
      );
      setAlertMessage({
        open: true,
        message: "User created successfully.",
        type: "success",
      });
      setTimeout(() => {
        if (onSuccess) {
          handleCancel();
          onSuccess();
        }
      }, 1500);
    } catch (err: any) {
      console.error("Error updating tenant user details:", err);
      const errMessage: string | JSX.Element =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Something went wrong!";
      setAlertMessage({
        open: true,
        message: errMessage,
        type: "error",
      });
      setTimeout(() => {
        handleCancel();
      }, 1000);
    } finally {
      setLoading(false);
    }
  };

  const showLoading = useMemo(() => loading, [loading]);
  useSetLoading(showLoading);
  const defaultInitialValues = {
    ...getInitialValues(rowData),
  };

  return (
    <>
      <div className="user-requests-create-main-wrapper">
        <div className="user-requests-create-popup-header">
          <h4 className="user-requests-create-popup-header-text">Add User</h4>
          <img
            src={closeIcon}
            alt="icon"
            className="roles-create-popup-close-button"
            onClick={handleCancel}
          />
        </div>
        <div className="user-requests-create-wrapper">
          <Formik
            initialValues={defaultInitialValues}
            validationSchema={validationSchema()}
            onSubmit={(values, { resetForm }) =>
              updateTenantUserDetails(values, resetForm)
            }
          >
            {({
              values,
              setFieldValue,
              handleSubmit,
              errors,
              touched,
              setFieldTouched,
            }) => (
              <Form>
                <div className="user-requests-add-user-form-wrapper">
                  <div className="user-requests-add-user-row">
                    <div className="user-requests-add-user-field">
                      <TextInputComponent
                        type="text"
                        fieldName="firstName"
                        label="First Name"
                        placeholder="Enter first name"
                        disabled={false}
                        value={values.firstName}
                        onChange={(e: any) =>
                          setFieldValue("firstName", e?.target?.value)
                        }
                        isAlphaNumeric={true}
                        minlength="3"
                        maxlength="50"
                        optional={false}
                      />
                    </div>
                    <div className="user-requests-add-user-field">
                      <TextInputComponent
                        type="text"
                        fieldName="lastName"
                        label="Last Name"
                        placeholder="Enter last name"
                        disabled={false}
                        value={values.lastName}
                        onChange={(e: any) =>
                          setFieldValue("lastName", e?.target?.value)
                        }
                        isAlphaNumeric={true}
                        minlength="3"
                        maxlength="50"
                        optional={false}
                      />
                    </div>
                  </div>
                  <div className="user-requests-add-user-row">
                    <div className="user-requests-add-user-field">
                      <div className="form-group-phone-number">
                        <CountryCodeWithPhoneNumber
                          fieldName="phoneNumber"
                          label="Phone Number"
                          placeholder="Phone Number"
                          onChange={(
                            value: string,
                            country: any,
                            one: any,
                            two: any
                          ) => {
                            const countryCode = country?.dialCode;

                            if (!countryCode) {
                              console.warn(
                                "Missing dialCode in country object"
                              );
                              return;
                            }

                            setFieldValue("phoneNumber", two);
                          }}
                          onBlur={() => {
                            setFieldTouched("phoneNumber", true);
                          }}
                          value={values?.phoneNumber || ""}
                          defaultCountry="us"
                          // countryCodeEditable
                        />
                      </div>
                      {touched.phoneNumber && touched?.phoneNumber && (
                        <div className="error-message">
                          {errors.phoneNumber}
                        </div>
                      )}
                    </div>
                    <div className="user-requests-add-user-field">
                      <TextInputComponent
                        type="email"
                        fieldName="email"
                        label="Email"
                        placeholder="Enter email"
                        disabled={false}
                        value={values.email}
                        onChange={(e: any) =>
                          setFieldValue("email", e?.target?.value)
                        }
                        optional={false}
                      />
                    </div>
                  </div>
                  <div className="user-requests-add-user-row role">
                    <RoleSelect
                      value={values.role}
                      onChange={(option) => setFieldValue("role", option)}
                      roles={roles}
                      touched={!!touched.role}
                      error={errors.role}
                      isLabelled
                    />
                  </div>
                </div>
                <div className="user-add-buttons-wrapper">
                  <button
                    type="button"
                    className="cancel-button"
                    onClick={handleCancel}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="cancel-button next-button-wrapper"
                    onClick={() => {
                      handleSubmit();
                    }}
                  >
                    Save
                  </button>
                </div>
                {alertMessage.open && (
                  <AlertComponent
                    message={alertMessage.message}
                    type={alertMessage.type || "info"}
                    open={alertMessage.open}
                    onClose={() => setAlertMessage({ open: false })}
                  />
                )}
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </>
  );
};

export default UserRequestsAddUser;
